import { getCSSFromStyleObject, getStyleObjectFromCSS } from '@lexical/selection';
import { $getRoot, $getSelection, $isRangeSelection, TextNode } from 'lexical';
import { isNil, omitBy } from 'lodash';
import * as BrandDefinition from 'modules/BrandDefinition';
import * as Models from 'models';
import {
  FontPluginStyle,
  getStyleObjectForFontSize,
  getBrandColorRefFromStyleObject,
  getStyleObjectForBrandColor,
  getStyleObjectForBrandFont,
  getBrandFontRefFromStyleObject,
} from '../FontPlugin/style';

export function getStyleObjectForBrandStyle(
  brandStyle: Models.BrandStyleMap | undefined,
  colors: Models.BrandColorsList,
  fonts: Models.BrandFontsList,
): Record<string, string> {
  const styles = BrandDefinition.getTextStylesFromBrandStyle(brandStyle, colors, fonts);
  // getTextStylesFromBrandStyle will currently return only the hex value instead of BrandColor
  const colorRef = BrandDefinition.tryToGetBrandColorRefByHEX(colors, styles[Models.TextBrandStyleField.FONT_COLOR]);
  const size = styles[Models.TextBrandStyleField.FONT_SIZE];
  const fontName = styles[Models.TextBrandStyleField.FONT_FAMILY];
  const characterStyleName = styles[Models.TextBrandStyleField.CHARACTER_STYLE];
  const fontRef = fontName ? { fontName, characterStyleName } : undefined;

  return omitBy({
    ...getStyleObjectForBrandFont(fonts, fontRef),
    ...getStyleObjectForBrandColor(colors, colorRef),
    ...getStyleObjectForFontSize(size),
  }, isNil);
}

export function $applyBrandStyleForFont(
  brandStyle: Models.BrandStyleMap | undefined,
  colors: Models.BrandColorsList,
  fonts: Models.BrandFontsList,
): void {
  const brandStyleObj = getStyleObjectForBrandStyle(brandStyle, colors, fonts);
  $getRoot().getAllTextNodes()
    .forEach((node: TextNode) => {
      const updatedStyles = {
        ...getStyleObjectFromCSS(node.getStyle()),
        ...brandStyleObj,
      };

      node.setStyle(getCSSFromStyleObject(updatedStyles));
    });
  const selection = $getSelection();
  if ($isRangeSelection(selection)) {
    const selectionStyle = getStyleObjectFromCSS(selection.style);
    selection.setStyle(getCSSFromStyleObject({ ...selectionStyle, ...brandStyleObj }));
  }
}

export function $verifyStyleForFont(
  colors: Models.BrandColorsList,
  fonts: Models.BrandFontsList,
): void {
  $getRoot().getAllTextNodes()
    .forEach((node) => {
      const style = getStyleObjectFromCSS(node.getStyle());
      const newStyle: Record<string, string> = {};
      const pushStyle = (name: string, value: string | undefined): void => {
        if (!value || style[name] === value) {
          return;
        }
        newStyle[name] = value;
      };

      const colorRef = getBrandColorRefFromStyleObject(style);
      if (colorRef) {
        pushStyle(FontPluginStyle.COLOR, BrandDefinition.getBrandColorValueHEX(colors, colorRef));
      }

      const fontRef = getBrandFontRefFromStyleObject(style);
      pushStyle(FontPluginStyle.FONT_FAMILY, BrandDefinition.getBrandFontValueFontFamily(fonts, fontRef));
      pushStyle(FontPluginStyle.FONT_STYLE, BrandDefinition.getBrandFontValueFontStyle(fonts, fontRef, false));
      pushStyle(FontPluginStyle.FONT_WEIGHT, BrandDefinition.getBrandFontValueFontWeight(fonts, fontRef, false));

      if (Object.keys(newStyle).length) {
        node.setStyle(getCSSFromStyleObject({ ...style, ...newStyle }));
      }
    });
}
